import React from "react";
import { FaExternalLinkAlt, FaTimes } from "react-icons/fa";
import { Route, RouteSection } from "../api/types";
import { toCoordString } from "../api/journeyApi.ts";
import { labelForSection, styleForSection } from "../tflColours.ts";
import Location from "./Location.ts";
import { SectionRef } from "./RouteLayer.tsx";

// The backend sends times of day as "HH:MM:SS".
const hhmm = (time: string | null): string => (time ? time.slice(0, 5) : "");

const SectionRow = ({
  section,
  selected,
  onHover,
  onToggle,
}: {
  section: RouteSection;
  selected: boolean;
  onHover: (hovered: boolean) => void;
  onToggle: () => void;
}) => {
  const style = styleForSection(section);
  const first = section.stops[0];
  const last = section.stops[section.stops.length - 1];
  const isWalk = section.mode === "walking";
  const stopCount = section.stops.length - 1;

  return (
    <div
      className={`flex cursor-pointer items-stretch rounded px-1 py-1 hover:bg-gray-100 ${
        selected ? "bg-gray-100" : ""
      }`}
      onMouseEnter={() => onHover(true)}
      onMouseLeave={() => onHover(false)}
      // Tap-to-highlight for touch screens, where hover doesn't exist.
      onClick={onToggle}
    >
      <span
        className="mr-2 w-1.5 shrink-0 rounded"
        style={{
          backgroundColor: style.dashArray ? undefined : style.color,
          // Dashed/dotted walking sections get a dotted border instead.
          border: style.dashArray ? `2px dotted ${style.color}` : undefined,
        }}
      />
      <div className="min-w-0">
        <p className="!m-0 text-sm font-medium text-gray-700">
          {labelForSection(section)}
          {section.departureTime && (
            <span className="pl-2 font-normal text-gray-500">
              {hhmm(section.departureTime)} → {hhmm(section.arrivalTime)}
            </span>
          )}
          <span className="pl-2 font-normal text-gray-500">
            {section.durationMinutes} min
          </span>
        </p>
        <p className="!m-0 truncate text-xs text-gray-500">
          {first.name || "Start"} → {last.name}
          {!isWalk && stopCount > 1 && ` · ${stopCount} stops`}
        </p>
      </div>
    </div>
  );
};

// Google-Maps-style directions panel: one block per starting location,
// one row per route section.
const RouteCard = ({
  routes,
  locations,
  dest,
  time,
  highlight,
  onHighlight,
  onClose,
}: {
  routes: Route[];
  locations: Location[];
  dest: Location;
  time: string;
  highlight: SectionRef | null;
  onHighlight: (ref: SectionRef | null) => void;
  onClose: () => void;
}) => {
  // The backend echoes the "lat,lng" string of each start; map it back to
  // the user-facing location name.
  const startName = (route: Route): string => {
    const loc = locations.find((l) => toCoordString(l) === route.startName);
    return loc ? loc.name : route.startName;
  };

  const googleMapsUrl = (route: Route): string => {
    // Today at the selected departure time. Google reads the 8j value as a
    // UTC wall-clock time, so shift by the timezone offset (otherwise a
    // 23:45 BST departure shows up as 22:45).
    const [hours, minutes] = time.split(":").map(Number);
    const departure = new Date();
    departure.setHours(hours, minutes, 0, 0);
    const timestamp = Math.floor(
      (departure.getTime() - departure.getTimezoneOffset() * 60000) / 1000,
    );

    // The documented "?api=1" URL form has no departure-time parameter, so
    // use the path form with the unofficial data blob: 6e0 = depart at,
    // 8j = unix timestamp, 3e3 = transit. If Google stops honouring it the
    // link still opens plain transit directions.
    return (
      "https://www.google.com/maps/dir/" +
      `${route.startName}/` +
      `${dest.coords.join(",")}/` +
      `data=!4m6!4m5!2m3!6e0!7e2!8j${timestamp}!3e3`
    );
  };

  // Sub-2-minute walks are noise; keep the original index for highlighting.
  const visibleSections = (
    route: Route,
  ): { section: RouteSection; sectionIdx: number }[] =>
    route.sections
      .map((section, sectionIdx) => ({ section, sectionIdx }))
      .filter(
        ({ section }) =>
          !(section.mode === "walking" && section.durationMinutes < 2),
      );

  return (
    <div className="absolute bottom-0 left-0 z-10000 w-full p-3 md:bottom-auto md:left-auto md:top-0 md:right-0 md:w-96">
      <div className="max-h-[50vh] overflow-y-auto rounded-lg border border-gray-200 bg-white p-3 shadow md:max-h-[90vh]">
        <div className="flex items-center justify-between pb-2">
          <h1 className="font-semibold text-gray-600">Directions</h1>
          <button
            className="text-gray-400 hover:text-gray-600"
            onClick={onClose}
          >
            <FaTimes />
          </button>
        </div>
        {routes.length === 0 && (
          <p className="!m-0 text-sm text-gray-500">No route found.</p>
        )}
        {routes.map((route, routeIdx) => (
          <div
            key={route.startName}
            className={routeIdx > 0 ? "mt-2 border-t border-gray-200 pt-2" : ""}
          >
            <div className="flex items-center justify-between pb-1">
              <p className="!m-0 truncate text-sm font-semibold text-gray-700">
                From {startName(route)}
                <span className="pl-2 font-normal text-gray-500">
                  {route.totalMinutes} min
                </span>
              </p>
              <a
                className="shrink-0 pl-2 text-xs text-blue-500 hover:underline"
                href={googleMapsUrl(route)}
                target="_blank"
                rel="noopener noreferrer"
              >
                Google Maps <FaExternalLinkAlt className="inline" size={10} />
              </a>
            </div>
            {visibleSections(route).map(({ section, sectionIdx }) => {
              const selected =
                highlight !== null &&
                highlight.routeIdx === routeIdx &&
                highlight.sectionIdx === sectionIdx;
              return (
                <SectionRow
                  key={sectionIdx}
                  section={section}
                  selected={selected}
                  onHover={(hovered) =>
                    onHighlight(hovered ? { routeIdx, sectionIdx } : null)
                  }
                  onToggle={() =>
                    onHighlight(selected ? null : { routeIdx, sectionIdx })
                  }
                />
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
};

export default RouteCard;
